/**
 * The one puja-image upload (admin docs 10 §3 and 24 §3.2): on the puja's own record, and on the
 * media library once the operator has said which puja the image is for. Either way it is minted
 * against that puja, so the library never holds an image nobody owns.
 */
import { useRef, useState } from 'react';
import { useConsole } from '../app/console';
import { ConfirmAction, ScopeAction } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import { CATALOG_IMAGE_TYPES, useUploadPujaImage } from '../lib/api/hooks/media';
import type { Scope } from '../lib/rbac';

/** The design's upload consequences (`screens-growth.jsx`), word for word. */
const UPLOAD_CONSEQUENCES = [
  'Uploads are scanned before they become usable — a scanning asset cannot be attached to a page.',
  'Derivatives are generated automatically; never upload pre-resized crops.',
  'Deity imagery is never composited with prices, offers or badges.',
];

/**
 * Upload from the record the image belongs to: each file is minted for this puja, sent straight
 * to storage and confirmed into the scan. It is attached below, from the library, once it is
 * ready — an upload never puts an unscanned file on a page.
 */
export function PujaImageUpload({
  scope,
  pujaId,
  note = 'New imagery is uploaded here, scanned, then attached from the library below.',
}: {
  scope: Scope;
  pujaId: number;
  /** What happens next, in the words of the screen it sits on. */
  note?: string;
}) {
  const { toast } = useConsole();
  const upload = useUploadPujaImage();
  const picker = useRef<HTMLInputElement>(null);
  const [confirming, setConfirming] = useState(false);

  const send = async (files: readonly File[]) => {
    let sent = 0;
    for (const file of files) {
      try {
        await upload.mutateAsync({ pujaId, file });
        sent += 1;
      } catch (e) {
        toast({ tone: 'error', message: `${file.name}: ${errorMessage(e)}` });
      }
    }
    if (sent > 0) {
      toast({ tone: 'info', message: `${sent === 1 ? 'Upload' : `${sent} uploads`} queued for scanning` });
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
      <span style={{ flex: 1, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
        {upload.isPending ? 'Uploading…' : note}
      </span>
      <ScopeAction
        scope={scope}
        need="catalogue_content"
        size="sm"
        variant="secondary"
        icon="upload"
        disabled={upload.isPending}
        onClick={() => setConfirming(true)}
      >
        Upload
      </ScopeAction>
      <input
        ref={picker}
        type="file"
        aria-label="Puja image files"
        accept={CATALOG_IMAGE_TYPES.join(',')}
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files!);
          e.target.value = '';
          void send(files);
        }}
      />
      <ConfirmAction
        open={confirming}
        title="Upload media?"
        phrase="UPLOAD"
        tone="warn"
        consequences={UPLOAD_CONSEQUENCES}
        confirmLabel="Choose files"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          picker.current!.click();
        }}
      />
    </div>
  );
}
